import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MessageCircle, Send, Loader2, ShieldAlert, ArrowLeft, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import EmojiPicker from "../components/EmojiPicker";

function timeShort(iso) {
  try {
    return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function PrivateMessages() {
  const { user, refresh } = useAuth();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [activePartner, setActivePartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [typing, setTyping] = useState(null); // {from_user_name}

  const partnerIsSupport = !!allUsers.find((u) => u.id === activePartner?.id)?.is_support;

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/private/conversations");
      setConversations(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setAllUsers(data.filter((u) => u.id !== user?.id));
    } catch (err) {
      console.error(err);
    }
  }, [user?.id]);

  useEffect(() => {
    loadConversations();
    loadUsers();
  }, [loadConversations, loadUsers]);

  // Resolve partner from URL
  useEffect(() => {
    if (!userId) {
      setActivePartner(null);
      return;
    }
    const fromConv = conversations.find((c) => c.user_id === userId);
    if (fromConv) {
      setActivePartner({ id: fromConv.user_id, name: fromConv.user_name, avatar_url: fromConv.user_avatar });
      return;
    }
    const fromUsers = allUsers.find((u) => u.id === userId);
    if (fromUsers) {
      setActivePartner({ id: fromUsers.id, name: fromUsers.name, avatar_url: fromUsers.avatar_url });
    }
  }, [userId, conversations, allUsers]);

  const loadMessages = useCallback(async (partnerId, silent = false) => {
    if (!partnerId) return;
    if (!silent) setLoadingMsgs(true);
    try {
      const { data } = await api.get(`/private/messages?with=${encodeURIComponent(partnerId)}`);
      setMessages(data);
    } catch (err) {
      console.error(err);
    } finally {
      if (!silent) setLoadingMsgs(false);
    }
  }, []);

  useEffect(() => {
    if (activePartner) {
      loadMessages(activePartner.id);
      // Beim Öffnen: Support-Mitarbeiter "betritt" den Chat mit Begrüßung, wenn leer
      api.post(`/private/open/${activePartner.id}`).catch(() => {});
    } else {
      setMessages([]);
    }
  }, [activePartner, loadMessages]);

  useEffect(() => {
    if (!activePartner) return;
    const id = setInterval(() => loadMessages(activePartner.id, true), 3000);
    return () => clearInterval(id);
  }, [activePartner, loadMessages]);

  // Typing-Indikator pollen
  useEffect(() => {
    if (!activePartner) {
      setTyping(null);
      return;
    }
    let cancelled = false;
    const fetchTyping = async () => {
      try {
        const { data } = await api.get(`/private/typing?with=${encodeURIComponent(activePartner.id)}`);
        if (!cancelled) setTyping(data.typing ? { name: data.from_user_name } : null);
      } catch {
        if (!cancelled) setTyping(null);
      }
    };
    fetchTyping();
    const id = setInterval(fetchTyping, 2500);
    return () => { cancelled = true; clearInterval(id); };
  }, [activePartner]);

  const send = async (e) => {
    e.preventDefault();
    const content = text.trim();
    if (!content || sending || !activePartner) return;
    setSending(true);
    try {
      const { data } = await api.post("/private/messages", {
        to_user_id: activePartner.id,
        content,
      });
      setMessages((prev) => [...prev, data]);
      setText("");
      loadConversations();
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error("Nachricht blockiert", { description: detail });
      refresh();
    } finally {
      setSending(false);
    }
  };

  const startChat = (partnerId) => {
    navigate(`/nachrichten/${partnerId}`);
    setShowNewChat(false);
  };

  return (
    <div data-testid="private-messages-page" className="space-y-3 sm:space-y-4">
      <div>
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#4DBFFF] mb-1">Persönlich</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tighter">
          Nachrichten
        </h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-0.5 sm:mt-1">Private Unterhaltungen mit anderen Fahrern.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-220px)] sm:h-[calc(100vh-280px)] min-h-[420px]">
        {/* Conversation list */}
        <aside
          className={`md:col-span-1 bg-[#FFFFFF] border border-slate-200 rounded-2xl overflow-hidden flex flex-col ${activePartner ? "hidden md:flex" : "flex"}`}
          data-testid="conversation-list"
        >
          <div className="p-3 border-b border-slate-200 flex items-center justify-between gap-2">
            <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Unterhaltungen</span>
            <button
              onClick={() => setShowNewChat((s) => !s)}
              data-testid="new-chat-toggle"
              className="text-xs bg-[#1D5BFF] hover:bg-[#1546C9] text-white font-semibold px-3 py-1.5 rounded-full transition-all"
            >
              + Neu
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingList ? (
              <div className="flex items-center justify-center py-12 text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Lade…
              </div>
            ) : showNewChat ? (
              <div className="p-2">
                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><UsersIcon className="w-3 h-3" /> Mitglied wählen</div>
                {allUsers.length === 0 ? (
                  <p className="text-sm text-slate-500 px-3 py-4">Keine anderen Mitglieder.</p>
                ) : (
                  allUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => startChat(u.id)}
                      data-testid={`start-chat-${u.id}`}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-100/70 rounded-xl text-left"
                    >
                      <img src={fileUrl(u.avatar_url)} alt={u.name} className="w-9 h-9 rounded-full object-cover border border-slate-200" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">{u.name}</div>
                        <div className="text-xs text-slate-500 truncate">{u.location || "—"}</div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center text-slate-500 py-12 px-6">
                <MessageCircle className="w-8 h-8 mb-2 text-[#4DBFFF]" />
                <p className="text-sm">Noch keine Unterhaltungen.</p>
                <p className="text-xs mt-1">Tippe „+ Neu" um zu starten.</p>
              </div>
            ) : (
              conversations.map((c) => (
                <button
                  key={c.user_id}
                  onClick={() => startChat(c.user_id)}
                  data-testid={`conversation-${c.user_id}`}
                  className={`w-full flex items-center gap-3 px-3 py-3 hover:bg-slate-100/70 text-left transition-colors ${activePartner?.id === c.user_id ? "bg-[#1D5BFF]/10 border-l-2 border-[#1D5BFF]" : ""}`}
                >
                  <img src={fileUrl(c.user_avatar)} alt={c.user_name} className="w-10 h-10 rounded-full object-cover border border-slate-200" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{c.user_name}</div>
                    <div className="text-xs text-slate-500 truncate">{c.last_message}</div>
                  </div>
                  {c.last_at && <span className="text-[10px] text-slate-600">{timeShort(c.last_at)}</span>}
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Chat panel */}
        <section
          className={`md:col-span-2 bg-[#FFFFFF] border border-slate-200 rounded-2xl overflow-hidden flex flex-col ${activePartner ? "flex" : "hidden md:flex"}`}
          data-testid="conversation-panel"
        >
          {!activePartner ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-500 px-6">
              <MessageCircle className="w-10 h-10 mb-3 text-[#4DBFFF]" />
              <p className="font-display text-lg text-slate-900">Wähle eine Unterhaltung</p>
              <p className="text-sm mt-1">oder starte eine neue.</p>
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-slate-200 flex items-center gap-3">
                <button onClick={() => navigate("/nachrichten")} className="md:hidden p-1.5 rounded-full hover:bg-slate-100/70" data-testid="back-to-list-btn">
                  <ArrowLeft className="w-4 h-4 text-slate-900" />
                </button>
                <div className="relative">
                  <img src={fileUrl(activePartner.avatar_url)} alt={activePartner.name} className="w-9 h-9 rounded-full object-cover border border-slate-200" />
                  {partnerIsSupport && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#FFFFFF] shadow-[0_0_8px_rgba(52,211,153,0.6)]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-slate-900 truncate">{activePartner.name}</div>
                  {partnerIsSupport && (
                    <div className="text-[10px] text-emerald-300 flex items-center gap-1 mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Aktiv
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {loadingMsgs ? (
                  <div className="h-full flex items-center justify-center text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Lade…
                  </div>
                ) : messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-slate-500 text-center">
                    <p className="text-sm">Sende die erste Nachricht an {activePartner.name}.</p>
                  </div>
                ) : (
                  <>
                    {messages.map((m) => {
                      if (m.is_system) {
                        return (
                          <div key={m.id} className="flex justify-center my-3" data-testid={`dm-system-${m.id}`}>
                            <div className="text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1 uppercase tracking-wider">
                              {m.content}
                            </div>
                          </div>
                        );
                      }
                      const own = m.from_user_id === user?.id;
                      return (
                        <div key={m.id} className={`msg-in flex w-full mb-3 ${own ? "justify-end" : "justify-start"}`}>
                          <div className="max-w-[78%]">
                            <div className={`px-4 py-3 rounded-[20px] text-[15px] leading-relaxed break-words ${own ? "bg-[#1D5BFF] text-white rounded-tr-md" : "bg-[#EEF3FA] text-slate-900 rounded-tl-md border border-slate-200/60"}`}>
                              {m.content}
                            </div>
                            <div className={`text-[10px] text-slate-500 mt-1 ${own ? "text-right" : ""}`}>{timeShort(m.created_at)}</div>
                          </div>
                        </div>
                      );
                    })}
                    {typing && (
                      <div className="flex justify-start mb-3" data-testid="typing-indicator">
                        <div className="bg-[#EEF3FA] border border-slate-200/60 rounded-[20px] rounded-tl-md px-4 py-3 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: "0ms"}} />
                          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: "150ms"}} />
                          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: "300ms"}} />
                          <span className="text-[11px] text-slate-600 ml-2">{typing.name} schreibt…</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <form onSubmit={send} className="border-t border-slate-200 bg-[#F5F8FD] p-3 flex items-end gap-2" data-testid="private-composer">
                <EmojiPicker onPick={(e) => setText((t) => t + e)} buttonClassName="p-3 rounded-2xl bg-[#FFFFFF] border border-slate-200 text-slate-700 hover:text-slate-900 hover:border-[#1D5BFF]/50 transition-all" />
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={`Nachricht an ${activePartner.name}…`}
                  data-testid="private-message-input"
                  className="flex-1 bg-[#FFFFFF] border border-slate-200 rounded-2xl px-4 py-3 text-slate-900 placeholder:text-slate-600 focus:outline-none focus:border-[#1D5BFF] focus:ring-2 focus:ring-[#1D5BFF]/30"
                />
                <button
                  type="submit"
                  disabled={sending || !text.trim()}
                  data-testid="private-send-btn"
                  className="bg-[#1D5BFF] hover:bg-[#1546C9] disabled:opacity-50 text-white p-3 rounded-full transition-all active:scale-95"
                >
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </form>
              <div className="px-3 pb-2 text-[10px] text-slate-600 flex items-center gap-1">
                <ShieldAlert className="w-3 h-3 text-[#4DBFFF]" /> Auch private Nachrichten werden moderiert.
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
